import type { ICameraVideoTrack } from 'agora-rtc-react';
import AgoraRTC, { IAgoraRTCClient } from 'agora-rtc-react';
import { StudioLogger } from '@studio/logger/StudioLogger';
import { AGORA_ENCODER_CONFIG } from '@studio/constants/VideoQualitySettings';
import {
  BACKGROUND_OPTIONS,
  getVFXConfig,
  teardownPipeline,
  transformVideo
} from '../virtualBackgrounds/virtualBackground';

const LOG = new StudioLogger('agoraVideoControl');

interface TransformableCameraTrack {
  transformedVideoTrack?: ICameraVideoTrack | null;
}

export interface VideoInput {
  videoTrack: ICameraVideoTrack | null;
  virtualBackground: BACKGROUND_OPTIONS;
  virtualBackgroundImageUrl: string;
  device: string;
  mirrored: boolean;
}

/**
 * Initializes a new video input with a camera device
 * Creates a camera video track with the specified device and default encoder configuration
 *
 * @param deviceId - The ID of the camera device to initialize
 * @returns Promise<VideoInput> - A video input object containing the video track, device ID, and default background settings
 * @throws {Error} If video track creation fails
 */
export const initializeVideoInput = async (deviceId: string): Promise<VideoInput> => {
  try {
    const videoTrack = await AgoraRTC.createCameraVideoTrack({
      cameraId: deviceId,
      encoderConfig: AGORA_ENCODER_CONFIG,
      optimizationMode: 'detail'
    });

    const videoInput = {
      videoTrack,
      device: deviceId,
      virtualBackground: BACKGROUND_OPTIONS.NONE,
      virtualBackgroundImageUrl: '',
      mirrored: true
    };

    LOG.info(`Initialized video input device: ${deviceId}`);
    return videoInput;
  } catch (error) {
    LOG.error(`Failed to initialize video input device ${deviceId}:`, error);
    throw error;
  }
};

/**
 * Discards a video track by unpublishing it from the Agora client
 * (if provided and currently published) and closing it.
 * Should be called whenever a track is being replaced with a new one.
 *
 * @param videoTrack - The track to discard
 * @param client - Optional Agora client to unpublish from
 */
const discardVideoTrack = async (videoTrack: ICameraVideoTrack, client?: IAgoraRTCClient): Promise<void> => {
  if (client) {
    if (client.localTracks?.includes(videoTrack)) {
      await client.unpublish(videoTrack);
      LOG.info('Unpublished old video track before discarding');
    }
  }
  videoTrack.close();
};

/**
 * Publishes a new video track to the Agora client if a client is provided.
 * Should be called after recreating a track to ensure remote participants
 * receive the new video stream.
 *
 * @param videoTrack - The new track to publish
 * @param client - Optional Agora client to publish to
 */
const publishVideoTrack = async (videoTrack: ICameraVideoTrack, client?: IAgoraRTCClient): Promise<void> => {
  if (client) {
    await client.publish(videoTrack);
    LOG.info('Published new video track to client');
  }
};

/**
 * Changes the camera device for an existing video input
 * If the device is already set, returns the current video input without changes
 * If no video track exists, creates a new video input with the specified device
 *
 * @param videoInput - The current video input object
 * @param deviceId - The ID of the new camera device to switch to
 * @param client - Optional Agora client, used to unpublish the old track when recreating
 * @returns Promise<VideoInput> - The updated video input object with the new device
 * @throws {Error} If device switching fails
 */
export const changeVideoInputDevice = async (
  videoInput: VideoInput,
  deviceId: string,
  client?: IAgoraRTCClient
): Promise<VideoInput> => {
  if (videoInput.device === deviceId) {
    LOG.info(`Video input device is already set to: ${deviceId}`);
    return videoInput;
  }
  try {
    if (videoInput.videoTrack === null) {
      LOG.warn('No video track available, now creating a new one with the selected device');
      return initializeVideoInput(deviceId);
    }

    // When virtual background is active, the track is piped through the VB processor.
    // Calling setDevice() on a piped track causes internal Agora SDK state issues
    // (MediaStreamTrackProcessor errors). Always recreate the track in this case
    // to get a clean pipeline.
    if (videoInput.virtualBackground !== BACKGROUND_OPTIONS.NONE) {
      LOG.info('Virtual background active, recreating track for clean pipeline switch');
      teardownPipeline(videoInput.videoTrack);

      await discardVideoTrack(videoInput.videoTrack, client);
      const newVideoInput = await initializeVideoInput(deviceId);

      if (newVideoInput.videoTrack) {
        LOG.info('Reapplying virtual background to newly created video track');
        const result = await applyVirtualBackgroundToTrack(
          newVideoInput.videoTrack,
          videoInput.virtualBackground,
          videoInput.virtualBackgroundImageUrl,
          deviceId,
          videoInput.mirrored
        );
        await publishVideoTrack(newVideoInput.videoTrack, client);
        return {
          ...newVideoInput,
          ...result
        };
      }

      return { ...newVideoInput, mirrored: videoInput.mirrored };
    }

    // No virtual background active — safe to use setDevice directly.
    // If setDevice fails (e.g. camera disconnected), fall back to recreating the track.
    try {
      await videoInput.videoTrack.setDevice(deviceId);
      const mediaStreamTrack = videoInput.videoTrack.getMediaStreamTrack();
      if (!mediaStreamTrack || mediaStreamTrack.readyState === 'ended') {
        LOG.warn(`setDevice resolved but MediaStreamTrack is ended for ${deviceId}, recreating track`);
        await discardVideoTrack(videoInput.videoTrack, client);
        const newVideoInput = await initializeVideoInput(deviceId);
        if (newVideoInput.videoTrack) {
          await publishVideoTrack(newVideoInput.videoTrack, client);
        }
        return { ...newVideoInput, mirrored: videoInput.mirrored };
      }
    } catch (setDeviceError) {
      LOG.warn(`setDevice failed for ${deviceId}, recreating track. Error: ${(setDeviceError as Error).message}`);
      await discardVideoTrack(videoInput.videoTrack, client);
      const newVideoInput = await initializeVideoInput(deviceId);
      if (newVideoInput.videoTrack) {
        await publishVideoTrack(newVideoInput.videoTrack, client);
      }
      return { ...newVideoInput, mirrored: videoInput.mirrored };
    }

    LOG.info(`Changed video input device to: ${deviceId}`);

    return {
      ...videoInput,
      device: deviceId
    };
  } catch (error) {
    LOG.error(`Failed to change video input device to ${deviceId}:`, error);
    throw error;
  }
};

/**
 * Applies or removes a virtual background effect to a video track
 * @param videoTrack - The video track to apply the background to
 * @param backgroundOption - The type of virtual background to apply (NONE, BLUR, or IMAGE)
 * @param backgroundImageUrl - The URL of the background image (required when backgroundOption is IMAGE)
 * @param deviceId - The ID of the current device
 * @param mirrored - Whether the video is mirrored (default: true)
 * @returns Promise<VideoInput> - Video input with updated track and background settings
 */
const applyVirtualBackgroundToTrack = async (
  videoTrack: ICameraVideoTrack,
  backgroundOption: BACKGROUND_OPTIONS,
  backgroundImageUrl: string,
  deviceId: string,
  mirrored = true
): Promise<VideoInput> => {
  const vfxConfig = await getVFXConfig(backgroundOption, backgroundImageUrl);
  if (vfxConfig === undefined) {
    teardownPipeline(videoTrack);

    return {
      videoTrack,
      device: deviceId,
      virtualBackground: BACKGROUND_OPTIONS.NONE,
      virtualBackgroundImageUrl: '',
      mirrored
    };
  }
  const updatedVideoInput = await transformVideo(videoTrack, vfxConfig, deviceId);
  LOG.info(`Changed user background to option: ${backgroundOption}`);
  return {
    ...updatedVideoInput,
    virtualBackground: backgroundOption,
    virtualBackgroundImageUrl: backgroundImageUrl,
    mirrored
  };
};

/**
 * Changes the virtual background for an existing video input
 * Applies blur, image, or removes background effects while maintaining the same camera device
 * If no video track exists, returns the current video input without changes
 *
 * @param videoInput - The current video input object
 * @param backgroundOption - The type of virtual background to apply (NONE, BLUR, or IMAGE)
 * @param backgroundImageUrl - The URL of the background image (required when backgroundOption is IMAGE, defaults to empty string)
 * @returns Promise<VideoInput> - The updated video input object with new background settings
 * @param client - Optional Agora client, used to unpublish old track and publish new one when recovering from stuck processorDestination
 * @throws {Error} If virtual background configuration or transformation fails
 */
export const changeUserBackground = async (
  videoInput: VideoInput,
  backgroundOption: BACKGROUND_OPTIONS,
  backgroundImageUrl = '',
  client?: IAgoraRTCClient
): Promise<VideoInput> => {
  if (videoInput.videoTrack === null) {
    LOG.warn('No video track available to change background');
    return videoInput;
  }
  if (
    videoInput.virtualBackground === backgroundOption &&
    videoInput.virtualBackgroundImageUrl === backgroundImageUrl
  ) {
    LOG.info(`User background is already set to option: ${backgroundOption}`);
    return videoInput;
  }

  if (videoInput.virtualBackground === BACKGROUND_OPTIONS.NONE && backgroundOption === BACKGROUND_OPTIONS.NONE) {
    LOG.info('User background is already set to NONE');
    return videoInput;
  }

  if (videoInput.virtualBackground === BACKGROUND_OPTIONS.BLUR && backgroundOption === BACKGROUND_OPTIONS.BLUR) {
    LOG.info('User background is already set to BLUR');
    return videoInput;
  }

  try {
    const result = await applyVirtualBackgroundToTrack(
      videoInput.videoTrack,
      backgroundOption,
      backgroundImageUrl,
      videoInput.device,
      videoInput.mirrored
    );
    return {
      ...videoInput,
      ...result
    };
  } catch (error) {
    // If piping failed because processorDestination is stuck (stale upstream from a
    // released processor — happens when camera fallback created a new track but the
    // component still holds a reference to the old one), the only recovery is to
    // recreate the track entirely to get a fresh processorDestination.
    if ((error as Error).message?.includes('already being piped')) {
      LOG.warn('ProcessorDestination stuck on stale track, recreating track to recover', {
        device: videoInput.device
      });
      teardownPipeline(videoInput.videoTrack);

      // Unpublish any currently published video track from the client.
      // The stale videoInput.videoTrack may differ from what's actually published
      // (e.g. the fallback handler created and published a different track).
      if (client) {
        const publishedVideoTracks = client.localTracks?.filter(t => t.trackMediaType === 'video') || [];
        for (const track of publishedVideoTracks) {
          await client.unpublish(track);
          (track as ICameraVideoTrack).close();
          LOG.info('Unpublished and closed stale video track during recovery');
        }
      }

      const newVideoInput = await initializeVideoInput(videoInput.device);
      if (newVideoInput.videoTrack) {
        const result = await applyVirtualBackgroundToTrack(
          newVideoInput.videoTrack,
          backgroundOption,
          backgroundImageUrl,
          newVideoInput.device,
          videoInput.mirrored
        );
        await publishVideoTrack(newVideoInput.videoTrack, client);
        return {
          ...videoInput,
          ...newVideoInput,
          ...result
        };
      }
      return { ...videoInput, ...newVideoInput };
    }
    LOG.error(`Failed to change user background to option ${backgroundOption}:`, error);
    throw error;
  }
};

/**
 * Toggles video for an Agora attendee by enabling/disabling the video track
 * @param videoTrack - The video track to toggle
 * @param enabled - Whether to enable (true) or disable (false) the video
 * @returns {Promise<ICameraVideoTrack | null>} - The video track if enabled, null if disabled or track not provided
 */
export const toggleAgoraVideo = async (
  videoTrack: ICameraVideoTrack | null,
  enabled: boolean,
  client: IAgoraRTCClient
): Promise<ICameraVideoTrack | null> => {
  try {
    LOG.info(`${enabled ? 'Enabling' : 'Disabling'} Agora video`, { enabled });

    const trackToToggle: ICameraVideoTrack | null =
      (videoTrack as TransformableCameraTrack)?.transformedVideoTrack ?? videoTrack;

    if (trackToToggle) {
      await trackToToggle.setEnabled(enabled);
      LOG.info(`Agora video track ${enabled ? 'enabled' : 'disabled'}`);
      enabled ? await client.publish(trackToToggle) : await client.unpublish(trackToToggle);
      return enabled ? trackToToggle : null;
    } else {
      LOG.info('No video track provided');
      return null;
    }
  } catch (error) {
    LOG.error(`Failed to ${enabled ? 'enable' : 'disable'} Agora video`, error);
    throw error;
  }
};

/**
 *
 * @param videoInput
 * @returns VideoInput with mirrored property toggled
 */
export const toggleMirrorVideo = (videoInput: VideoInput): VideoInput => {
  if (videoInput.mirrored !== undefined) {
    return {
      ...videoInput,
      mirrored: !videoInput.mirrored
    };
  }
  return videoInput;
};
